import { forwardRef, useEffect, useMemo, useRef, useState, version as reactVersion } from 'react';
import { formatUptime, heapMB, systemInfo, useUptime } from '../editor/telemetry';
import { STATS, topTools } from '../editor/buffers';
import { wrapLine } from '../editor/wrap';

const INLINE = /(`[^`]+`)|\[([^\]]+)\]\(([^)]+)\)/g;
const JSON_TOKEN = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?)|\b(true|false|null)\b|([{}[\],])/g;

function Link({ href, children }) {
    const external = /^https?:/.test(href);
    return (
        <a
            className="md-link"
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
            {children}
        </a>
    );
}

function inline(text, keyPrefix) {
    const out = [];
    let last = 0;
    let m;
    INLINE.lastIndex = 0;
    while ((m = INLINE.exec(text))) {
        if (m.index > last) out.push(text.slice(last, m.index));
        if (m[1]) {
            out.push(<span key={`${keyPrefix}-${m.index}`} className="md-code">{m[1]}</span>);
        } else {
            out.push(
                <span key={`${keyPrefix}-${m.index}`}>
                    <span className="md-punct">[</span>
                    <Link href={m[3]}>{m[2]}</Link>
                    <span className="md-punct">]</span>
                </span>
            );
        }
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

function markdownLine(text, key, state) {
    if (text.startsWith('```')) {
        state.fence = !state.fence;
        return <span className="md-fence">{text}</span>;
    }
    if (state.fence) return <span className="md-code">{text}</span>;
    if (text.startsWith('# ')) return <span className="md-h1">{text}</span>;
    if (/^#{2,6} /.test(text)) return <span className="md-h">{text}</span>;
    if (text.startsWith('> ')) return <span className="md-quote">{text}</span>;
    if (text.startsWith('<!--')) return <span className="md-comment">{text}</span>;
    if (text.startsWith('- ')) {
        return (
            <>
                <span className="md-list">-</span> {inline(text.slice(2), key)}
            </>
        );
    }
    return inline(text, key);
}

function jsonLine(text, key) {
    const out = [];
    let last = 0;
    let m;
    JSON_TOKEN.lastIndex = 0;
    while ((m = JSON_TOKEN.exec(text))) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const k = `${key}-${m.index}`;
        if (m[1] && m[2]) {
            out.push(<span key={k} className="js-key">{m[1]}</span>, <span key={`${k}c`} className="js-punct">{m[2]}</span>);
        } else if (m[1]) {
            out.push(<span key={k} className="js-str">{m[1]}</span>);
        } else if (m[3] || m[4]) {
            out.push(<span key={k} className="md-num">{m[0]}</span>);
        } else {
            out.push(<span key={k} className="js-punct">{m[0]}</span>);
        }
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

// Real metadata for a loaded image: pixel size, bytes over the wire, fetch time.
function imageMeta(img) {
    const meta = { w: img.naturalWidth, h: img.naturalHeight };
    const src = img.currentSrc || img.src;
    if (src.startsWith('data:')) {
        meta.kb = Math.round((src.length * 3) / 4 / 1024);
        meta.inline = true;
    } else {
        const entry = performance.getEntriesByName?.(src)?.[0];
        if (entry) {
            const bytes = entry.decodedBodySize || entry.encodedBodySize || entry.transferSize;
            if (bytes) meta.kb = Math.round(bytes / 1024);
            meta.ms = Math.round(entry.duration);
            meta.cached = entry.transferSize === 0 && entry.decodedBodySize > 0;
        }
    }
    meta.type = (src.match(/^data:image\/(\w+)/) || src.match(/\.(\w+)(?:\?|$)/) || [])[1];
    return meta;
}

function Shot({ line }) {
    const [meta, setMeta] = useState(null);
    const img = (
        <img
            src={line.img}
            alt={line.alt}
            loading="lazy"
            onLoad={(e) => setMeta(imageMeta(e.currentTarget))}
        />
    );
    const details = meta && [
        `${meta.w}×${meta.h}`,
        meta.type,
        meta.kb != null && `${meta.kb} KB`,
        meta.inline ? 'inlined' : meta.cached ? 'cached' : meta.ms != null && `${meta.ms} ms`,
    ].filter(Boolean);
    return (
        <figure className="shot">
            <figcaption>
                <span>
                    {line.caption}
                    {details && <span className="shot-meta"> · {details.join(' · ')}</span>}
                </span>
                {line.link && <span>open ↗</span>}
            </figcaption>
            <div className="frame">
                <span className="crop crop-tl" aria-hidden="true" />
                <span className="crop crop-tr" aria-hidden="true" />
                <span className="crop crop-bl" aria-hidden="true" />
                <span className="crop crop-br" aria-hidden="true" />
                <span className="scan" aria-hidden="true" />
                {line.link ? (
                    <a href={line.link} target="_blank" rel="noopener noreferrer" aria-label={`Open ${line.alt}`}>
                        {img}
                    </a>
                ) : (
                    img
                )}
            </div>
        </figure>
    );
}

const GAUGE = 24;

function Prompt({ line, last }) {
    return (
        <div className="term term-prompt">
            <p>
                <span className="t-user">adeboye</span>
                <span className="t-dim">@</span>
                <span className="t-host">dev</span> <span className="t-path">{line.path}</span>
            </p>
            <p>
                <span className="t-caret">›</span> <span className="t-cmd">{line.cmd}</span>
                {last && <span className="t-cursor" aria-hidden="true" />}
            </p>
        </div>
    );
}

function KeyValue({ line }) {
    const filled = line.bar != null ? Math.max(1, Math.round(line.bar * GAUGE)) : 0;
    return (
        <p className={`term term-kv ${line.wide ? 'is-wide' : ''}`}>
            <span className="kv-k">{line.k}</span>
            <span className="kv-v">{line.v}</span>
            {line.bar != null && (
                <span className="kv-bar" aria-hidden="true">
                    <span className="on">{'█'.repeat(filled)}</span>
                    <span className="off">{'░'.repeat(GAUGE - filled)}</span>
                </span>
            )}
            {line.note && <span className="kv-note">{line.note}</span>}
        </p>
    );
}

function Links({ line }) {
    return (
        <p className={`term term-links ${line.label ? 'term-kv' : ''}`}>
            {line.label && <span className="kv-k">{line.label}</span>}
            <span className="links">
                {line.items.map((item) => (
                    <Link key={item.href} href={item.href}>{item.label}</Link>
                ))}
            </span>
        </p>
    );
}

function Commit({ line }) {
    return (
        <div className="term commit">
            <p className="commit-head">
                <span className="commit-node" aria-hidden="true" />
                <span className="commit-year">{line.year}</span>
                <span className="dimmed"> · </span>
                <span className="commit-company">{line.company}</span>
            </p>
            <article className="commit-card">
                <h2>{line.role}</h2>
                <p className="commit-text">{line.text}</p>
                <p className="commit-tags">
                    {line.tags.map((t) => (
                        <span key={t}>{t}</span>
                    ))}
                </p>
            </article>
        </div>
    );
}

function Cards({ line }) {
    return (
        <ul className="term term-cards">
            {line.items.map((item) => (
                <li key={item.href}>
                    <a href={item.href} className="pcard">
                        <span className="pcard-shot">
                            {item.image && <img src={item.image} alt="" loading="lazy" />}
                        </span>
                        <span className="pcard-name">
                            {item.label}
                            {item.live && <span className="pcard-live" title="live">●</span>}
                        </span>
                        <span className="pcard-note">{item.note}</span>
                    </a>
                </li>
            ))}
        </ul>
    );
}

const LOGO = [
    '  ▄██████▄   ████████▄ ',
    ' ███    ███  ███    ███',
    ' ███    ███  ███    ███',
    ' ██████████  █████████ ',
    ' ███    ███  ███    ███',
    ' ███    ███  ███    ███',
    ' ███    ███  █████████▀',
]
const SWATCHES = ['bg', 'line', 'dim', 'kw', 'accent', 'ty', 'fn', 'num', 'str', 'ink'];

// neofetch, but every value is read from the visitor's browser at runtime.
function Neofetch() {
    const uptime = useUptime();
    const sys = useMemo(systemInfo, []);
    const heap = heapMB();
    const rows = [
        ['os', `${sys.os} · ${sys.browser}`],
        ['kernel', `react ${reactVersion}`],
        ['uptime', `${formatUptime(uptime)} (this session)`],
        ['packages', `${STATS.projects} projects · ${STATS.tools} tools`],
        ['stack', topTools(3).map((t) => t.name.toLowerCase()).join(', ')],
        ['display', `${sys.screen} @${sys.dpr}x · viewport ${sys.viewport}`],
        ['cpu', sys.cores && `${sys.cores} logical cores`],
        ['memory', [sys.memory && `${sys.memory} GB device`, heap && `${heap} MB js heap`].filter(Boolean).join(' · ') || null],
        ['network', sys.network],
        ['load', sys.loadMs != null && `DOMContentLoaded in ${sys.loadMs} ms`],
        ['locale', `${sys.lang} · ${sys.tz}`],
    ].filter(([, v]) => v);
    return (
        <div className="term neofetch">
            <pre className="nf-logo" aria-hidden="true">{LOGO.join('\n')}</pre>
            <div className="nf-info">
                <p>
                    <span className="t-user">adeboye</span>
                    <span className="t-dim">@</span>
                    <span className="t-user">dev</span>
                </p>
                <p className="t-dim">{'-'.repeat(11)}</p>
                {rows.map(([k, v]) => (
                    <p key={k}>
                        <span className="nf-k">{k}</span>
                        <span className="nf-sep">: </span>
                        {v}
                    </p>
                ))}
                <p className="nf-swatches" aria-hidden="true">
                    {SWATCHES.map((c) => (
                        <span key={c} style={{ background: `var(--${c})` }} />
                    ))}
                </p>
            </div>
        </div>
    );
}

const TILDES = 24;

const MAX_COLS = 80;

// Columns that fit in the text area, measured from the rendered cell width.
function useColumns(ref, gutterCells) {
    const [cols, setCols] = useState(MAX_COLS);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const probe = document.createElement('span');
        probe.textContent = '0'.repeat(20);
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre';
        el.appendChild(probe);
        const measure = () => {
            const cell = probe.getBoundingClientRect().width / 20 || 7.2;
            const usable = Math.floor(el.clientWidth / cell) - gutterCells;
            setCols(Math.max(24, Math.min(MAX_COLS, usable)));
        };
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        measure();
        return () => {
            ro.disconnect();
            probe.remove();
        };
    }, [ref, gutterCells]);
    return cols;
}

const NO_WRAP = /^(#|>|<!--|```)/;

const Buffer = forwardRef(function Buffer({ buffer, onScroll, rnu = false }, outerRef) {
    const innerRef = useRef(null);
    const [cursor, setCursor] = useState(1);
    const term = buffer.lang === 'term';
    const narrow = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches;
    const cols = useColumns(innerRef, term ? 4 : narrow ? 6 : 8);
    const lines = useMemo(
        () =>
            buffer.lang === 'json'
                ? buffer.lines
                : buffer.lines.flatMap((l) => (typeof l === 'string' && !NO_WRAP.test(l) ? wrapLine(l, cols) : [l])),
        [buffer, cols]
    );
    const setRef = (el) => {
        innerRef.current = el;
        if (typeof outerRef === 'function') outerRef(el);
        else if (outerRef) outerRef.current = el;
    };
    const state = { fence: false };
    let number = 0;
    const lastIndex = lines.length - 1;
    return (
        <div className={`buffer ${term ? 'is-term' : ''}`} ref={setRef} tabIndex={0} onScroll={onScroll} aria-label={buffer.path}>
            {lines.map((line, i) => {
                if (typeof line === 'object') {
                    if (line.t === 'prompt') return <Prompt key={i} line={line} last={i === lastIndex} />;
                    if (line.t === 'kv') return <KeyValue key={i} line={line} />;
                    if (line.t === 'links') return <Links key={i} line={line} />;
                    if (line.t === 'commit') return <Commit key={i} line={line} />;
                    if (line.t === 'cards') return <Cards key={i} line={line} />;
                    if (line.t === 'neofetch') return <Neofetch key={i} />;
                    return <Shot key={i} line={line} />;
                }
                if (term) {
                    return (
                        <p className="term term-out" key={i}>
                            {line === '' ? ' ' : inline(line, i)}
                        </p>
                    );
                }
                number += 1;
                const n = number;
                const content = buffer.lang === 'json' ? jsonLine(line, i) : markdownLine(line, i, state);
                const shown = rnu && n !== cursor ? Math.abs(n - cursor) : n;
                return (
                    <div className={`ln ${n === cursor ? 'is-cursor' : ''}`} key={i} onMouseEnter={() => setCursor(n)}>
                        <span className="no">{shown}</span>
                        <span className="tx">{content}</span>
                    </div>
                );
            })}
            {!term && Array.from({ length: TILDES }, (_, i) => (
                <div className="ln is-tilde" key={`t${i}`} aria-hidden="true">
                    <span className="no" />
                    <span className="tx">~</span>
                </div>
            ))}
        </div>
    );
});

export default Buffer;
